/**
 * Blog post: TELC B1 Schreiben Guide with structure, scoring criteria,
 * Redemittel and a full model email.
 * SEO target: "telc b1 schreiben", "telc b1 brief schreiben", "b1 schreiben
 * beispiel" (sister post to the ranking telc-b1 Redemittel page).
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function TelcB1SchreibenGuidePost() {
  const lang = useLangParam();

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>By the Logami team. Built by German teachers, checked against the official telc scoring criteria.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        In the <strong>telc B1 Schreiben</strong> section you have 30 minutes to write one letter or email.
        It sounds simple, and that is exactly the trap: most points are lost on missed guiding points and
        wrong register, not on grammar. This guide covers the task format, how examiners score your text,
        a clear paragraph template, and a complete model email you can adapt in the exam.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The task format
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        You get one writing situation, usually a <strong>personal or semi-formal letter or email</strong>:
        answering a friend, writing to a course organizer, or responding to an invitation. The task lists
        <strong> four guiding points (Leitpunkte)</strong>, and you should deal properly with
        <strong> at least three of them</strong>. There is no official word minimum, but
        <strong> 120 to 150 words</strong> is a sensible target: enough to develop three points, short
        enough to stay in control.
      </p>
      <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02] mb-10">
        <h3 className="font-space text-lg font-bold text-black mb-3">Quick facts</h3>
        <ul className="list-disc pl-5 text-base text-black/70 leading-relaxed space-y-2">
          <li>30 minutes, one letter or email</li>
          <li>Address at least 3 of the 4 Leitpunkte</li>
          <li>Match the register: du-form for friends, Sie-form for organizations</li>
          <li>Anrede and Grußformel are required and scored</li>
        </ul>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How your letter is scored
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        telc raters use three criteria. Content: did you cover the Leitpunkte with real sentences, not just
        a mention? Communicative design: correct Anrede and Grußformel, sensible paragraphs, connectors like
        <em> deshalb</em>, <em>trotzdem</em>, and <em>außerdem</em>. Formal accuracy: B1-level grammar and
        spelling. A text that covers three points cleanly in simple, correct German scores higher than an
        ambitious text full of half-finished B2 constructions.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The four-paragraph template
      </h2>
      <ol className="list-decimal pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li><strong>Anrede:</strong> <em>Liebe Maria,</em> for friends, <em>Sehr geehrte Damen und Herren,</em> for organizations</li>
        <li><strong>Einleitung (1 to 2 sentences):</strong> react to the situation. <em>Vielen Dank für deine E-Mail! Ich habe mich sehr gefreut.</em></li>
        <li><strong>Hauptteil (3 short paragraphs):</strong> one Leitpunkt each, connected with <em>außerdem</em>, <em>deshalb</em>, <em>leider</em></li>
        <li><strong>Schluss + Grußformel:</strong> a closing sentence with an outlook, then <em>Viele Grüße</em> or <em>Mit freundlichen Grüßen</em> and your name</li>
      </ol>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Model email: answering a friend
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Task: your friend Maria has invited you to her birthday party on Saturday. Thank her, say whether
        you are coming, offer to help, and ask what you can bring.
      </p>
      <div className="p-5 sm:p-6 rounded-2xl border-2 border-black bg-white mb-6 font-medium text-black/80 leading-relaxed text-base">
        <p className="mb-4">Liebe Maria,</p>
        <p className="mb-4">
          vielen Dank für deine Einladung! Ich habe mich sehr über deine E-Mail gefreut.
        </p>
        <p className="mb-4">
          Natürlich komme ich am Samstag zu deiner Party. Ich freue mich schon darauf, deine Freunde
          kennenzulernen. Leider kann ich erst um 19 Uhr da sein, weil ich am Nachmittag noch arbeiten
          muss. Ich hoffe, das ist kein Problem.
        </p>
        <p className="mb-4">
          Wenn du möchtest, helfe ich dir gern bei der Vorbereitung. Ich könnte zum Beispiel schon um
          18 Uhr kommen und dir beim Aufbauen helfen. Sag mir außerdem bitte, was ich mitbringen soll.
          Soll ich vielleicht einen Salat oder etwas zu trinken besorgen?
        </p>
        <p className="mb-4">
          Ich freue mich sehr auf Samstag!
        </p>
        <p className="mb-1">Viele Grüße</p>
        <p>Deine Anna</p>
      </div>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        This email covers all four points in about 110 words, keeps a consistent du-register, uses B1
        connectors (<em>leider, weil, außerdem, zum Beispiel</em>), and closes properly. That is a
        full-score performance without a single complicated sentence.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The mistakes that cost the most points
      </h2>
      <ul className="list-disc pl-5 text-base md:text-lg text-black/70 leading-relaxed space-y-3 mb-10">
        <li><strong>Skipping a Leitpunkt.</strong> Each ignored point loses content marks directly.</li>
        <li><strong>Mixing du and Sie.</strong> Choose the register in the first line and keep it.</li>
        <li><strong>No Anrede or Grußformel.</strong> These frame the letter and are explicitly scored.</li>
        <li><strong>Memorized filler.</strong> Raters recognize memorized universal sentences that ignore the task.</li>
        <li><strong>No final check.</strong> Keep 3 minutes for verb endings and word order.</li>
      </ul>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How to practice
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Learn the standard phrases from the
        {' '}<Link to={`/${lang}/blog/telc-b1-redemittel-schreiben`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">telc B1 Redemittel collection</Link>{' '}
        until they come automatically, then write one email every second day under exam conditions. In the
        {' '}<Link to={`/${lang}/mock-exams/telc-b1`} className="font-bold text-black underline underline-offset-2 hover:opacity-80">Logami telc B1 mock exam</Link>{' '}
        your letter is scored on the same three criteria described above, so you always know whether you
        are ready.
      </p>

      <RelatedResources provider="telc" level="b1" variant="exam-guide" />
    </article>
  );
}
